import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  useToast,
  Card,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  Progress,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiRefreshCw,
  FiAward,
  FiCheck,
  FiX,
} from 'react-icons/fi';
import { canvasAPI } from '../../../services/api';

const StudentsTab = ({ 
  students, 
  loading, 
  syncing, 
  onSyncGrades, 
  onRefresh, 
  onCertificatesCreated,
  defaultDuration = '40 horas',
  contextTitle = '',
  settings = null
}) => {
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [certificateFilter, setCertificateFilter] = useState('all');
  const [isCreating, setIsCreating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Estados para job asíncrono
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  
  // Estado para valores de variables personalizadas por curso
  const [customCourseValues, setCustomCourseValues] = useState({});
  
  // Estado para variables requeridas por la plantilla seleccionada
  const [templateVariablesRequired, setTemplateVariablesRequired] = useState([]);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [courseName, setCourseName] = useState('');
  const [duration, setDuration] = useState('40 horas');
  
  const toast = useToast();

  // Filtrar estudiantes
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      // Búsqueda por nombre/email
      const matchesSearch = searchQuery === '' || 
        student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (student.email && student.email.toLowerCase().includes(searchQuery.toLowerCase()));

      // Filtro por calificación
      const matchesGrade = gradeFilter === 'all' ||
        (gradeFilter === 'passed' && student.final_grade >= 70) ||
        (gradeFilter === 'failed' && student.final_grade < 70);

      // Filtro por certificado
      const matchesCertificate = certificateFilter === 'all' ||
        (certificateFilter === 'with' && student.has_certificate) ||
        (certificateFilter === 'without' && !student.has_certificate);

      return matchesSearch && matchesGrade && matchesCertificate;
    });
  }, [students, searchQuery, gradeFilter, certificateFilter]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Seleccionar solo estudiantes sin certificado
      const eligibleStudents = filteredStudents
        .filter(s => !s.has_certificate)
        .map(s => s.user_id);
      setSelectedStudents(eligibleStudents);
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (userId, hasCertificate) => {
    if (hasCertificate) {
      toast({
        title: 'Ya tiene certificado',
        description: 'Este estudiante ya tiene un certificado emitido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSelectedStudents(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleOpenCreateModal = () => {
    if (selectedStudents.length === 0) {
      toast({
        title: 'Sin estudiantes seleccionados',
        description: 'Selecciona al menos un estudiante para crear certificados',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Establecer valores predeterminados al abrir el modal
    console.log('🔧 Opening modal with defaults:', { contextTitle, defaultDuration });
    setCourseName(contextTitle || '');
    setDuration(defaultDuration || '40 horas');
    
    // Cargar plantillas disponibles
    loadTemplates();
    
    onOpen();
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await canvasAPI.getInstructorTemplates();
      console.log('📋 Templates loaded:', response);
      setTemplates(response || []);
      
      // Seleccionar plantilla predeterminada si existe
      const defaultTemplate = response.find(t => t.is_default);
      console.log('🎯 Default template found:', defaultTemplate);
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
        console.log('🔧 Calling updateTemplateVariables with:', defaultTemplate);
        updateTemplateVariables(defaultTemplate);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Advertencia',
        description: 'No se pudieron cargar las plantillas de certificados',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Actualizar variables requeridas cuando cambia la plantilla
  const updateTemplateVariables = (template) => {
    console.log('🔍 updateTemplateVariables called with template:', template);
    console.log('🔍 variables_used:', template?.variables_used);
    console.log('🔍 settings.custom_variables:', settings?.custom_variables);
    
    if (!template || !template.variables_used || !template.variables_used.variables) {
      console.log('⚠️ No template or variables_used found, clearing requirements');
      setTemplateVariablesRequired([]);
      return;
    }

    // Filtrar solo variables personalizadas de tipo "course"
    if (!settings?.custom_variables) {
      console.log('⚠️ No custom_variables in settings, clearing requirements');
      setTemplateVariablesRequired([]);
      return;
    }

    const courseVarsRequired = template.variables_used.variables
      .filter(varName => {
        const varConfig = settings.custom_variables[varName];
        console.log(`  📝 Checking variable "${varName}":`, varConfig);
        return varConfig && varConfig.type === 'course';
      })
      .map(varName => ({
        name: varName,
        config: settings.custom_variables[varName]
      }));

    console.log('✅ Course variables required:', courseVarsRequired);
    setTemplateVariablesRequired(courseVarsRequired);
  };

  const handleCreateCertificates = async () => {
    if (!courseName.trim()) {
      toast({
        title: 'Nombre del curso requerido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validar variables requeridas
    const missingVars = templateVariablesRequired.filter(
      v => !customCourseValues[v.name] || customCourseValues[v.name].trim() === ''
    );

    if (missingVars.length > 0) {
      toast({
        title: 'Variables requeridas faltantes',
        description: `Debes proporcionar valores para: ${missingVars.map(v => v.name).join(', ')}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsCreating(true);
      
      // Iniciar job de creación
      const response = await canvasAPI.bulkCreateCertificates(
        selectedStudents,
        courseName,
        duration,
        selectedTemplateId,
        Object.keys(customCourseValues).length > 0 ? customCourseValues : null
      );

      const { job_id, total_students } = response;
      
      if (!job_id) {
        throw new Error('No se recibió job_id del servidor');
      }

      setCurrentJobId(job_id);
      setJobProgress({
        status: 'pending',
        processed: 0,
        total: total_students,
        created: 0,
        skipped: 0,
        failed: 0,
        percentage: 0
      });

      toast({
        title: 'Procesando certificados',
        description: `Creando ${total_students} certificado(s). Esto puede tardar unos minutos...`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      // Iniciar polling del estado del job
      startJobPolling(job_id);
      
    } catch (error) {
      console.error('Error creating certificates:', error);
      setIsCreating(false);
      setCurrentJobId(null);
      setJobProgress(null);
      
      // Manejar errores de validación (400)
      if (error.response?.status === 400 && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Si es un objeto con missing_fields (error de validación)
        if (typeof detail === 'object' && detail.missing_fields) {
          const missingList = detail.missing_fields.join('\n• ');
          const warningsList = detail.warnings?.length > 0 ? `\n\nAdvertencias:\n• ${detail.warnings.join('\n• ')}` : '';
          
          toast({
            title: '⚠️ Configuración incompleta',
            description: `${detail.message}\n\nCampos faltantes:\n• ${missingList}${warningsList}`,
            status: 'warning',
            duration: 10000,
            isClosable: true,
          });
        } else {
          // Error genérico
          toast({
            title: 'Error',
            description: typeof detail === 'string' ? detail : detail.message || 'No se pudo crear los certificados',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        // Otro tipo de error
        toast({
          title: 'Error',
          description: error.response?.data?.detail || error.message || 'No se pudo iniciar la creación de certificados',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Polling del estado del job
  const startJobPolling = (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await canvasAPI.getCertificateJobStatus(jobId);
        
        // Actualizar progreso
        setJobProgress({
          status: status.status,
          processed: status.progress.processed,
          total: status.progress.total,
          created: status.progress.created,
          skipped: status.progress.skipped,
          failed: status.progress.failed,
          percentage: status.progress.percentage
        });

        // Si el job terminó
        if (['completed', 'failed', 'partial'].includes(status.status)) {
          clearInterval(pollInterval);
          setIsCreating(false);
          
          if (status.status === 'completed') {
            toast({
              title: '✅ Certificados creados exitosamente',
              description: `${status.progress.created} creados, ${status.progress.skipped} omitidos`,
              status: 'success',
              duration: 7000,
              isClosable: true,
            });
          } else if (status.status === 'partial') {
            toast({
              title: '⚠️ Certificados creados con advertencias',
              description: `${status.progress.created} creados, ${status.progress.skipped} omitidos, ${status.progress.failed} fallidos`,
              status: 'warning',
              duration: 10000,
              isClosable: true,
            });
          } else {
            toast({
              title: '❌ Error en la creación',
              description: status.error_message || 'Ocurrió un error al crear los certificados',
              status: 'error',
              duration: 10000,
              isClosable: true,
            });
          }

          // Limpiar y refrescar
          setCurrentJobId(null);
          setJobProgress(null);
          setSelectedStudents([]);
          setCourseName('');
          setDuration('40 horas');
          setSelectedTemplateId(null);
          onClose();

          // Refrescar datos
          if (onCertificatesCreated) {
            await onCertificatesCreated();
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        clearInterval(pollInterval);
        setIsCreating(false);
        setCurrentJobId(null);
        setJobProgress(null);
        toast({
          title: 'Error',
          description: 'No se pudo obtener el estado del procesamiento',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }, 2000); // Poll cada 2 segundos
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando estudiantes...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Actions */}
            <HStack spacing={3} flexWrap="wrap">
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={onSyncGrades}
                isLoading={syncing}
                loadingText="Sincronizando..."
                colorScheme="blue"
                variant="outline"
              >
                Sincronizar Calificaciones
              </Button>
              <Button
                leftIcon={<FiAward />}
                onClick={handleOpenCreateModal}
                colorScheme="green"
                isDisabled={selectedStudents.length === 0}
              >
                Crear Certificados ({selectedStudents.length})
              </Button>
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={onRefresh}
                variant="ghost"
              >
                Actualizar
              </Button>
            </HStack>

            {/* Filters */}
            <HStack spacing={3} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar estudiante..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Select
                maxW="200px"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <option value="all">Todas las calificaciones</option>
                <option value="passed">Aprobados (≥70)</option>
                <option value="failed">Reprobados (&lt;70)</option>
              </Select>

              <Select
                maxW="200px"
                value={certificateFilter}
                onChange={(e) => setCertificateFilter(e.target.value)}
              >
                <option value="all">Todos los certificados</option>
                <option value="with">Con certificado</option>
                <option value="without">Sin certificado</option>
              </Select>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Students Table */}
      {filteredStudents.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No se encontraron estudiantes con los filtros aplicados
        </Alert>
      ) : (
        <Card>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Checkbox
                      isChecked={
                        selectedStudents.length > 0 &&
                        selectedStudents.length === filteredStudents.filter(s => !s.has_certificate).length
                      }
                      onChange={handleSelectAll}
                      isIndeterminate={
                        selectedStudents.length > 0 &&
                        selectedStudents.length < filteredStudents.filter(s => !s.has_certificate).length
                      }
                    />
                  </Th>
                  <Th>Estudiante</Th>
                  <Th>Email</Th>
                  <Th isNumeric>Calificación Final</Th>
                  <Th>Estado</Th>
                  <Th>Certificado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredStudents.map((student) => (
                  <Tr key={student.user_id}>
                    <Td>
                      <Checkbox
                        isChecked={selectedStudents.includes(student.user_id)}
                        onChange={() => handleSelectStudent(student.user_id, student.has_certificate)}
                        isDisabled={student.has_certificate}
                      />
                    </Td>
                    <Td fontWeight="medium">{student.name}</Td>
                    <Td color="gray.600">{student.email || 'N/A'}</Td>
                    <Td isNumeric>
                      <Badge
                        colorScheme={student.final_grade >= 70 ? 'green' : 'red'}
                        fontSize="md"
                        px={2}
                        py={1}
                      >
                        {student.final_grade ? student.final_grade.toFixed(1) : 'N/A'}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Icon
                          as={student.final_grade >= 70 ? FiCheck : FiX}
                          color={student.final_grade >= 70 ? 'green.500' : 'red.500'}
                        />
                        <Text fontSize="sm">
                          {student.final_grade >= 70 ? 'Aprobado' : 'Reprobado'}
                        </Text>
                      </HStack>
                    </Td>
                    <Td>
                      {student.has_certificate ? (
                        <Badge colorScheme="blue">
                          {student.certificate_id || 'Emitido'}
                        </Badge>
                      ) : (
                        <Badge colorScheme="gray">Sin certificado</Badge>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      )}

      {/* Create Certificates Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Crear Certificados</ModalHeader>
          <ModalCloseButton isDisabled={isCreating} />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info">
                <AlertIcon />
                Se crearán certificados para {selectedStudents.length} estudiante(s)
              </Alert>

              {/* Mostrar progreso si está procesando */}
              {isCreating && jobProgress && (
                <Box width="100%">
                  <Alert status={
                    jobProgress.status === 'completed' ? 'success' :
                    jobProgress.status === 'failed' ? 'error' :
                    'info'
                  } mb={3}>
                    <AlertIcon />
                    <VStack align="start" spacing={1} flex="1">
                      <Text fontWeight="bold">
                        {jobProgress.status === 'pending' && 'Iniciando...'}
                        {jobProgress.status === 'processing' && 'Procesando certificados...'}
                        {jobProgress.status === 'completed' && 'Completado'}
                        {jobProgress.status === 'failed' && 'Error'}
                        {jobProgress.status === 'partial' && 'Completado con advertencias'}
                      </Text>
                      <Text fontSize="sm">
                        {jobProgress.processed} de {jobProgress.total} estudiantes procesados
                      </Text>
                      {jobProgress.status === 'processing' && (
                        <HStack fontSize="xs" color="gray.600" spacing={4}>
                          <Text>✓ Creados: {jobProgress.created}</Text>
                          <Text>⊘ Omitidos: {jobProgress.skipped}</Text>
                          <Text>✗ Fallidos: {jobProgress.failed}</Text>
                        </HStack>
                      )}
                    </VStack>
                  </Alert>
                  <Progress 
                    value={jobProgress.percentage} 
                    size="lg" 
                    colorScheme="green" 
                    hasStripe 
                    isAnimated
                    mb={4}
                  />
                </Box>
              )}

              <FormControl isRequired isDisabled={isCreating}>
                <FormLabel>Nombre del Curso</FormLabel>
                <Input
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="Ej: Introducción a Python"
                />
              </FormControl>

              <FormControl isRequired isDisabled={isCreating}>
                <FormLabel>Duración</FormLabel>
                <Input
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="Ej: 40 horas"
                />
              </FormControl>

              <FormControl isDisabled={isCreating}>
                <FormLabel>Tipo de Certificado</FormLabel>
                {loadingTemplates ? (
                  <Spinner size="sm" />
                ) : templates.length > 0 ? (
                  <Select
                    value={selectedTemplateId || ''}
                    onChange={(e) => {
                      const newTemplateId = e.target.value ? parseInt(e.target.value) : null;
                      setSelectedTemplateId(newTemplateId);
                      
                      // Actualizar variables requeridas
                      if (newTemplateId) {
                        const selectedTemplate = templates.find(t => t.id === newTemplateId);
                        if (selectedTemplate) {
                          updateTemplateVariables(selectedTemplate);
                        }
                      } else {
                        setTemplateVariablesRequired([]);
                      }
                    }}
                    placeholder="Selecciona un tipo de certificado"
                  >
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.is_default ? '(Predeterminado)' : ''}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No hay plantillas disponibles. Se usará la plantilla predeterminada.
                  </Text>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Selecciona el tipo de diploma (participación, aprobación, etc.)
                </Text>
              </FormControl>

              {/* Variables personalizadas por curso - Solo las requeridas por la plantilla */}
              {console.log('🎨 Rendering form - templateVariablesRequired:', templateVariablesRequired)}
              {templateVariablesRequired.length > 0 && (
                <Box width="100%" p={4} borderWidth="1px" borderRadius="md" bg="purple.50">
                  <HStack mb={3}>
                    <Text fontSize="md" fontWeight="bold" color="purple.700">
                      Variables Requeridas para Esta Plantilla
                    </Text>
                    <Badge colorScheme="purple">Obligatorias</Badge>
                  </HStack>
                  <VStack spacing={3} align="stretch">
                    {templateVariablesRequired.map(({ name: varName, config }) => (
                      <FormControl 
                        key={varName} 
                        isDisabled={isCreating}
                        isRequired
                      >
                        <FormLabel fontSize="sm" color="purple.700">
                          {varName}
                          {config.description && (
                            <Text as="span" fontSize="xs" color="gray.600" ml={2} fontWeight="normal">
                              ({config.description})
                            </Text>
                          )}
                        </FormLabel>
                        <Input
                          size="sm"
                          value={customCourseValues[varName] || ''}
                          onChange={(e) => setCustomCourseValues({
                            ...customCourseValues,
                            [varName]: e.target.value
                          })}
                          placeholder={config.example || config.value || `Ingrese ${varName}`}
                          borderColor={
                            !customCourseValues[varName] || customCourseValues[varName].trim() === ''
                              ? 'red.300'
                              : 'gray.200'
                          }
                        />
                        {config.example && (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Ejemplo: {config.example}
                          </Text>
                        )}
                      </FormControl>
                    ))}
                  </VStack>
                  <Alert status="info" mt={3} fontSize="xs">
                    <AlertIcon />
                    Estos valores se aplicarán a todos los certificados de este lote
                  </Alert>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onClose}
              isDisabled={isCreating}
            >
              {isCreating ? 'Procesando...' : 'Cancelar'}
            </Button>
            <Button
              colorScheme="green"
              onClick={handleCreateCertificates}
              isLoading={isCreating}
              loadingText={jobProgress ? `${jobProgress.percentage}%` : "Iniciando..."}
              isDisabled={isCreating}
            >
              Crear Certificados
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StudentsTab;
